import PoiMapCard from 'components/cards/poi/PoiMapCard';
import { clusterCountLayer, clusterLayer, unclusteredPointLayer } from 'components/maps/layers';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMapGL, { FlyToInterpolator, GeolocateControl, Layer, NavigationControl, Popup, Source } from 'react-map-gl';
import useSWR from 'swr';

import LoadingIndicator from '../LoadingIndicator';
import mapImages from './images';
import { initialViewport, mapStyles } from './settings';

function PoiMap({ pois = [], isLoading, viewportPreset }) {
    const [viewport, setViewport] = useState(
        viewportPreset ? { ...initialViewport, ...viewportPreset } : initialViewport
    );
    const [hoveredPoi, setHoveredPoi] = useState(null);
    const [selectedPoi, setSelectedPoi] = useState(null);
    const mapRef = useRef();
    const { data, isValidating: isFetchingPoi } = useSWR(
        selectedPoi?.id ? `/public/pois/${selectedPoi.id}?include=areas,type,thumbs` : null
    );

    const fetchedPoi = data?.data;

    const points = useMemo(
        () =>
            pois.map((poi) => ({
                type: 'Feature',
                properties: {
                    cluster: false,
                    id: poi.id,
                    poi_type_slug: poi.type?.slug,
                    slug: poi.slug,
                    name: poi.name,
                    type: poi.type
                },
                geometry: {
                    type: 'Point',
                    coordinates: [poi.location.lng, poi.location.lat]
                }
            })),
        [pois]
    );

    useEffect(() => {
        if (typeof viewportPreset === 'object') {
            setViewport({
                ...viewport,
                ...viewportPreset,
                transitionInterpolator: new FlyToInterpolator({
                    speed: 2
                }),
                transitionDuration: 'auto'
            });
        }
    }, [viewportPreset]);

    const mapLoaded = useCallback(() => {
        const map = mapRef.current?.getMap();
        if (map) {
            loadImages(map);
            // map.setMaxBounds([
            //     [12.079765405116632, 45.042766915784156],
            //     [18.304804124468063, 47.671623182580056]
            // ]);

            // map.addSource('mapbox-dem', {
            //     type: 'raster-dem',
            //     url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
            //     bounds: [13.38454737935946, 45.410738948407875, 16.80041632369428, 46.94906979113417]
            // });
            // // add the DEM source as a terrain layer with exaggerated height
            // map.setTerrain({ source: 'mapbox-dem', exaggeration: 1 });
        }
    }, []);

    const loadImages = useCallback((map) => {
        for (let i = 0; i < mapImages.length; i++) {
            const image = mapImages[i];
            if (!mapRef.current) continue;
            map.loadImage(image.src, (error, imageData) => {
                if (error) throw error;
                if (!mapRef.current) return;
                if (!map.hasImage(image.key)) map.addImage(image.key, imageData);
            });
        }
    }, []);

    const onClick = useCallback((event) => {
        const feature = event.features[0];
        if (!feature) return;

        if (feature.layer.id === 'clusters') {
            const clusterId = feature.properties.cluster_id;
            return zoomToCluster(feature, clusterId);
        }
        if (feature.layer.id === 'pois') {
            const { properties } = feature;
            setSelectedPoi({
                ...feature.properties,
                type: JSON.parse(properties.type),
                longitude: feature.geometry.coordinates[0],
                latitude: feature.geometry.coordinates[1]
            });
        }
    }, []);

    const zoomToCluster = useCallback((feature, clusterId) => {
        const source = mapRef.current.getMap().getSource('pois');
        source.getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err || !zoom) {
                return;
            }

            setViewport({
                ...viewport,
                longitude: feature.geometry.coordinates[0],
                latitude: feature.geometry.coordinates[1],
                zoom: zoom + 2,
                transitionInterpolator: new FlyToInterpolator({
                    speed: 2
                }),
                transitionDuration: 'auto'
            });
        });
    }, []);

    const onHover = useCallback((event) => {
        const poi = event.features && event.features[0];
        if (poi) {
            setHoveredPoi({
                id: poi && poi.properties.id,
                longitude: event.lngLat[0],
                latitude: event.lngLat[1],
                name: poi && poi.properties.name
            });
        }
    }, []);

    const handleViewportChange = useCallback((newViewport) => {
        setViewport({ ...newViewport });
    }, []);

    return (
        <div className="relative w-full h-full">
            {isLoading && (
                <div className="absolute z-50 top-4 w-full">
                    <div className="flex items-center justify-center">
                        <LoadingIndicator />
                    </div>
                </div>
            )}
            <ReactMapGL
                {...viewport}
                width="100%"
                height="100%"
                mapStyle={mapStyles[0].url}
                maxZoom={26}
                mapboxApiAccessToken={process.env.NEXT_PUBLIC_MAPBOX_TOKEN}
                onViewportChange={handleViewportChange}
                interactiveLayerIds={[clusterLayer.id, unclusteredPointLayer.id]}
                onClick={onClick}
                onLoad={mapLoaded}
                onHover={onHover}
                attributionControl={false}
                touchRotate={false}
                ref={mapRef}
                disableTokenWarning={typeof window !== 'undefined' && window.location.hostname !== 'localhost'}
            >
                <NavigationControl style={{ right: 15, top: 15 }} />
                <GeolocateControl
                    style={{ left: 15, top: 15 }}
                    positionOptions={{ enableHighAccuracy: true }}
                    trackUserLocation
                />
                <Source
                    id="pois"
                    type="geojson"
                    data={{
                        type: 'FeatureCollection',
                        features: points
                    }}
                    cluster
                    clusterMaxZoom={20}
                    clusterRadius={40}
                >
                    <Layer {...clusterLayer} />
                    <Layer {...clusterCountLayer} />
                    <Layer {...unclusteredPointLayer} />
                    <Layer
                        id="sky"
                        type="sky"
                        paint={{
                            'sky-type': 'atmosphere',
                            'sky-atmosphere-sun': [0.0, 0.0],
                            'sky-atmosphere-sun-intensity': 15
                        }}
                    />
                </Source>

                {hoveredPoi?.name && hoveredPoi?.id !== selectedPoi?.id && (
                    <Popup
                        longitude={hoveredPoi.longitude}
                        latitude={hoveredPoi.latitude}
                        closeButton={false}
                        className="text-sm font-sans"
                    >
                        <span className="inline-block py-1 px-4"> {hoveredPoi.name}</span>
                    </Popup>
                )}

                {selectedPoi?.name && (
                    <Popup
                        longitude={selectedPoi.longitude}
                        latitude={selectedPoi.latitude}
                        closeButton={false}
                        className="text-sm font-sans rounded-xl w-64 md:w-72"
                        offsetTop={-35}
                    >
                        {isFetchingPoi && (
                            <div className="w-full h-36 flex items-center justify-center">
                                <LoadingIndicator />
                            </div>
                        )}
                        {fetchedPoi && (
                            <PoiMapCard
                                images={fetchedPoi.thumbs}
                                title={fetchedPoi.name}
                                link={`/ideja/${fetchedPoi.slug}`}
                                location={
                                    fetchedPoi.areas
                                        ? `${fetchedPoi.areas.find((area) => area.type.key === 'municipality').name}, ${
                                              fetchedPoi.areas.find((area) => area.type.key === 'region').name
                                          }`
                                        : ''
                                }
                                handleClose={() => setSelectedPoi(null)}
                            />
                        )}
                    </Popup>
                )}
            </ReactMapGL>
        </div>
    );
}

export default PoiMap;
